import { useCallback, useEffect, useState } from "react";

/**
 * Global collapsible-sidebar state.
 *
 * - `collapsed` (desktop): persisted in localStorage so the preference
 *   survives page refresh, login, and module switches.
 * - `mobileOpen` (mobile/tablet <lg): ephemeral overlay state, not persisted.
 *   The overlay is closed on route change by the consumer.
 *
 * All consumers share the same store via a tiny listener registry so
 * multiple ModuleShell instances (rare, but safe) stay in sync.
 */
const STORAGE_KEY = "owl.sidebar.collapsed";
const listeners = new Set();
let current = null;

function readStored() {
  if (current !== null) return current;
  try {
    current = window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    current = false;
  }
  return current;
}

function writeStored(val) {
  current = !!val;
  try {
    window.localStorage.setItem(STORAGE_KEY, current ? "1" : "0");
  } catch {
    /* ignore quota / privacy-mode errors */
  }
  listeners.forEach((fn) => fn(current));
}

export function useSidebar() {
  const [collapsed, setCollapsed] = useState(readStored);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const listener = (v) => setCollapsed(v);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  const toggleCollapsed = useCallback(() => writeStored(!readStored()), []);
  const setCollapsedExplicit = useCallback((v) => writeStored(v), []);
  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);

  return {
    collapsed,
    mobileOpen,
    toggleCollapsed,
    setCollapsed: setCollapsedExplicit,
    openMobile,
    closeMobile,
    toggleMobile,
  };
}
